import { ServiceTitanIconLink } from "@/core/components/service-titan-icon-link";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { InvoiceCardTitleLink, StatusBadge } from "@/accounts-payable/common";
import type { ApAutomatedRunItem } from "@/accounts-payable/types";
import {
  AP_TAB,
  formatApDate,
  invoiceStatusMeta,
  stJobUrl,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";
import { cn } from "@/core/utils/utils";
import { AutomatedActionRow } from "./automated-action-row";

export const AutomatedInvoiceCard = ({
  item,
  onOpenInvoice,
}: {
  item: ApAutomatedRunItem;
  onOpenInvoice: () => void;
}) => {
  const actions = item.automated_agent_run.actions;
  return (
    <Stack
      gap="none"
      fullWidth
      className="overflow-hidden rounded-lg border border-stroke-tertiary bg-card pb-2"
    >
      {/* Invoice header */}
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap="md"
        fullWidth
        className="flex-wrap bg-bg-secondary/50 px-6 py-4"
      >
        <Stack gap="none" className="min-w-0 flex-1">
          {/* Line one: the invoice number is the record, so it leads; the
              status badge closes it. */}
          <Stack direction="row" gap="sm" align="center" className="flex-wrap">
            <InvoiceCardTitleLink
              invoiceId={item.id}
              vendorName={item.vendor_name}
              invoiceNumber={item.invoice_number}
              fromTab={AP_TAB.automated}
              className="text-lg font-semibold no-underline hover:underline"
            />
            <StatusBadge meta={invoiceStatusMeta(item)} />
          </Stack>
          {/* Line two: the paperwork metadata, with the vendor between the date
              and the address, and the amount pinned to the end (hidden when
              there is none). Sorted by created_at (arrival), like Review. */}
          <Stack direction="row" gap="md" align="center" className="mt-1.5">
            <Stack
              direction="row"
              gap="xs"
              align="center"
              className="min-w-0 flex-wrap text-md text-tertiary-text"
            >
              {item.st_job_external_id && (
                <>
                  <ServiceTitanIconLink
                    url={stJobUrl(item.st_job_external_id)}
                    label={`Open job #${item.st_job_external_id} in ServiceTitan`}
                    className="ml-0 underline underline-offset-2"
                  >
                    Job #{item.st_job_external_id}
                  </ServiceTitanIconLink>
                  <span aria-hidden>·</span>
                </>
              )}
              <span>{`Received ${formatApDate(item.created_at)}`}</span>
              <span aria-hidden>·</span>
              <span className="truncate">{item.vendor_name}</span>
              {item.service_address && (
                <>
                  <span aria-hidden>·</span>
                  <span className="truncate">{item.service_address}</span>
                </>
              )}
            </Stack>
            {item.total_amount_cents ? (
              <Stack
                direction="row"
                gap="md"
                align="center"
                className="flex-none"
              >
                <span className="h-5 w-px bg-stroke-tertiary" aria-hidden />
                <span
                  className={cn(
                    "text-md font-semibold tabular-nums text-primary-text",
                    { "text-negative-text": item.total_amount_cents < 0 },
                  )}
                >
                  {formatCentsOrDashIfZero(item.total_amount_cents)}
                </span>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
        {/* Explicit affordance into the invoice, and the home for future
            invoice-level controls (retry, view in ServiceTitan). */}
        <Button
          size="xsPill"
          variant="tertiary"
          className="flex-none"
          onClick={onOpenInvoice}
        >
          View invoice
        </Button>
      </Stack>

      {/* What the run did — inset divided rows, matching the Review tab. */}
      {actions.length > 0 && (
        <Stack gap="none" fullWidth>
          {actions.map((a, i) => (
            <AutomatedActionRow key={a.id} action={a} showDivider={i > 0} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};
